"use client";

import { Square } from "lucide-react";
import { useEffect, useState } from "react";

import { cx } from "./cx";
import { IconButton } from "./icon-button";
import styles from "./recording-strip.module.css";

type State = "recording" | "paused" | "idle";

export interface RecordingStripProps {
  state?: State;
  /** Elapsed seconds. Formatted internally as mm:ss. */
  durationSec: number;
  onStop?: () => void;
  ariaLabel?: string;
  bars?: number;
  className?: string;
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

interface WaveProps {
  active: boolean;
  bars: number;
}

function Wave({ active, bars }: WaveProps) {
  const [levels, setLevels] = useState<number[]>(() =>
    new Array(bars).fill(0.3),
  );

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => {
      setLevels((prev) => prev.map(() => 0.15 + Math.random() * 0.85));
    }, 100);
    return () => window.clearInterval(id);
  }, [active]);

  const last = levels.length - 1;
  return (
    <span className={styles.wave} aria-hidden>
      {levels.map((l, i) => {
        const distFromLead = last - i;
        const leadBoost =
          distFromLead === 0 ? 0.35 : distFromLead === 1 ? 0.18 : 0;
        const baseOpacity = active ? 0.55 + l * 0.35 : 0.3;
        return (
          <span
            key={i}
            className={styles.bar}
            style={{
              height: `${Math.max(2, l * 18)}px`,
              opacity: Math.min(1, baseOpacity + leadBoost),
            }}
          />
        );
      })}
    </span>
  );
}

export function RecordingStrip({
  state = "recording",
  durationSec,
  onStop,
  ariaLabel = "Stop recording",
  bars = 12,
  className,
}: RecordingStripProps) {
  return (
    <div
      className={cx(styles.strip, className)}
      data-state={state}
      role="status"
      aria-live="polite"
      aria-label={`Recording ${formatTime(durationSec)}`}
    >
      <IconButton
        size="md"
        tone="danger"
        appearance="solid"
        aria-label={ariaLabel}
        className={cx(
          styles.stopBtn,
          state === "recording" && styles.stopBtnPulse,
        )}
        onClick={onStop}
      >
        <Square size={10} fill="currentColor" strokeWidth={0} />
      </IconButton>
      <Wave active={state === "recording"} bars={bars} />
      <span className={styles.timer}>{formatTime(durationSec)}</span>
    </div>
  );
}
